/**
 * Localization Index
 *
 * Exports all translation files and provides type-safe utilities
 */

import type { LanguageCode } from '@/lib/language';
import en from './en.json';
import zh from './zh.json';
import ar from './ar.json';
import de from './de.json';
import fr from './fr.json';
import es from './es.json';
import pt from './pt.json';
import nl from './nl.json';
import it from './it.json';
import pl from './pl.json';

// Type for translation keys
export type TranslationKey = keyof typeof en;

// All translations mapped by language code
export const translations: Record<LanguageCode, typeof en> = {
  en,
  zh,
  ar,
  de,
  fr,
  es,
  pt,
  nl,
  it,
  pl,
};

/**
 * Get translation for a specific key path
 * @example t('header.login', 'en') // "Log in"
 */
export function getTranslation(
  key: string,
  language: LanguageCode,
  params?: Record<string, string | number>
): string {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  let value: any;

  const langTranslations = translations[language];

  // Strategy 1: Check if first segment is a section name and rest is an exact key
  // Handles cases like: toolStatus.Retrieving your information...
  const firstDotIndex = key.indexOf('.');
  if (firstDotIndex > 0 && langTranslations && typeof langTranslations === 'object') {
    const sectionName = key.substring(0, firstDotIndex);
    const remainingKey = key.substring(firstDotIndex + 1);

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const section = (langTranslations as any)[sectionName];
    if (section && typeof section === 'object' && remainingKey in section) {
      const exactMatch = section[remainingKey];
      if (typeof exactMatch === 'string') {
        value = exactMatch;
      }
    }
  }

  // Strategy 2: Try exact key lookup without section prefix (for backward compatibility)
  if (!value && langTranslations && typeof langTranslations === 'object') {
    for (const section of Object.values(langTranslations)) {
      if (section && typeof section === 'object' && key in section) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const exactMatch = (section as any)[key];
        if (typeof exactMatch === 'string') {
          value = exactMatch;
          break;
        }
      }
    }
  }

  // Strategy 3: Try dot-separated path resolution
  if (!value) {
    const keys = key.split('.');
    value = translations[language];

    for (const k of keys) {
      if (value && typeof value === 'object' && k in value) {
        value = value[k];
      } else {
        // Fallback to English
        value = translations.en;

        // Try section-based exact key lookup in English
        const firstDotIndex = key.indexOf('.');
        let foundExactInEn = false;

        if (firstDotIndex > 0 && value && typeof value === 'object') {
          const sectionName = key.substring(0, firstDotIndex);
          const remainingKey = key.substring(firstDotIndex + 1);

          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          const section = (value as any)[sectionName];
          if (section && typeof section === 'object' && remainingKey in section) {
            const exactMatch = section[remainingKey];
            if (typeof exactMatch === 'string') {
              value = exactMatch;
              foundExactInEn = true;
            }
          }
        }

        // Try exact key lookup in English (all sections)
        if (!foundExactInEn && value && typeof value === 'object') {
          for (const section of Object.values(value)) {
            if (section && typeof section === 'object' && key in section) {
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
              const exactMatch = (section as any)[key];
              if (typeof exactMatch === 'string') {
                value = exactMatch;
                foundExactInEn = true;
                break;
              }
            }
          }
        }

        if (!foundExactInEn) {
          // Fall back to dot-path in English
          for (const fallbackKey of keys) {
            if (value && typeof value === 'object' && fallbackKey in value) {
              value = value[fallbackKey];
            } else {
              return key; // Return key itself if even English doesn't have it
            }
          }
        }
        break;
      }
    }
  }

  // If value is not a string, return the key
  if (typeof value !== 'string') {
    return key;
  }

  // Replace parameters if provided
  if (params) {
    return Object.entries(params).reduce(
      (text, [param, paramValue]) =>
        text.replace(new RegExp(`\\{${param}\\}`, 'g'), String(paramValue)),
      value
    );
  }

  return value;
}

export { en, zh, ar, de, fr, es, pt, nl, it, pl };
